import { DestroyRef, inject, Injectable } from '@angular/core';

import { associate } from '@ps-gui/shared-utils';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ExtractedRow, ExtractedTableCell } from '../../models/extracted-values';
import { ExtractedRowForm, ExtractedTableCellForm } from '../../models/extraction-form-models';
import { TableModel } from '../../models/extraction-models/extraction-models';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { parseValue } from '../../utils/type-parsers';
import { createValidator } from '@ps-gui/ui';

@Injectable({
  providedIn: 'root',
})
export class TableFormService {
  private readonly formBuilder = inject(FormBuilder);

  createRowForm(
    documentId: string | undefined,
    extractedRow: ExtractedRow,
    tableModel: TableModel,
    callbackOnFormGroupValueChange: () => void,
    destroyRef: DestroyRef,
  ): ExtractedRowForm {
    const membership = extractedRow?.rowMembership ?? (documentId ? { documentId } : undefined);

    const isEmpty$ = new BehaviorSubject(this.isRowEmpty(extractedRow.rowData));

    const rowFormGroup = this.formBuilder.nonNullable.group({
      rowData: this.createRowDataFormGroup(tableModel, extractedRow, isEmpty$),
      class: this.formBuilder.control(extractedRow?.class),
      rowMembership: this.formBuilder.control(membership),
    }) as ExtractedRowForm;

    rowFormGroup.valueChanges
      .pipe(
        takeUntilDestroyed(destroyRef),
        map((rowFormValue) => this.isRowEmpty(rowFormValue.rowData!)),
        distinctUntilChanged(),
      )
      .subscribe(isEmpty$);

    isEmpty$
      .pipe(takeUntilDestroyed(destroyRef), distinctUntilChanged())
      .subscribe(() =>
        Object.values(rowFormGroup.controls.rowData.controls).forEach((cellForm) =>
          cellForm.controls.value.updateValueAndValidity(),
        ),
      );

    rowFormGroup.valueChanges
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => callbackOnFormGroupValueChange());

    return rowFormGroup;
  }

  private createRowDataFormGroup(
    tableModel: TableModel,
    extractedRow: ExtractedRow,
    isEmpty$: BehaviorSubject<boolean>,
  ): FormGroup<Record<string, ExtractedTableCellForm>> {
    return this.formBuilder.group(
      associate(
        tableModel.fieldModels,
        (model) => model.key,
        (model) => {
          const parsedValue = extractedRow.rowData[model.key]?.value
            ? parseValue(String(extractedRow.rowData[model.key].value), model).value
            : null;

          return this.formBuilder.group({
            value: this.formBuilder.control(parsedValue, {
              validators: model.validators?.map((config) => {
                const mappedValidator = createValidator(config);
                return (control) => (isEmpty$.value ? null : mappedValidator(control));
              }),
            }),
            id: this.formBuilder.nonNullable.control(
              extractedRow.rowData[model.key]?.id ?? crypto.randomUUID(),
            ),
          });
        },
      ),
    );
  }

  private readonly isRowEmpty = (rowData: Partial<Record<string, Partial<ExtractedTableCell>>>) =>
    Object.values(rowData).every(
      (rowData) => rowData?.value === null || rowData?.value === undefined || rowData.value === '',
    );
}
